import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';

const ComplaintsModule = () => {
    const [view, setView] = useState('list'); // 'list', 'create', 'details'
    const [complaints, setComplaints] = useState([]);
    const [stats, setStats] = useState({ total: 0, in_process: 0, satisfied: 0 });
    const [selectedComplaint, setSelectedComplaint] = useState(null);
    const [followups, setFollowups] = useState([]);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCustomerType, setFilterCustomerType] = useState(''); // New State for United / Outsider filter
    const [searchQuery, setSearchQuery] = useState('');

    // Categories & Bike Search States
    const [categories, setCategories] = useState([]);
    const [bikeSearchQuery, setBikeSearchQuery] = useState('');
    const [bikeSuggestions, setBikeSuggestions] = useState([]);

    // Form States
    const [formData, setFormData] = useState({
        customer_name: '', 
        customer_phone: '', 
        customer_type: 'United',
        complaint_type: '', 
        description: '',
        bike_master_id: null,
        make: '', 
        model_name: '', 
        color: '', 
        engine_number: '', 
        chassis_number: '', 
        model_year: ''
    });

    // New Timeline Step Form
    const [newStep, setNewStep] = useState({ step_title: '', comment: '', mark_satisfied: false });

    // Fetch Categories from Server on Mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await API.get('/v1/complaints/categories');
                if (res.data.success) setCategories(res.data.data);
            } catch (err) { console.error(err); }
        };
        fetchCategories();
    }, []);

    const fetchComplaints = useCallback(async () => {
        try {
            const res = await API.get('/v1/complaints');
            if (res.data.success) {
                let data = res.data.data || [];

                // Frontend Search Filter (Ticket #, Name, Phone)
                if (searchQuery.trim() !== '') {
                    const q = searchQuery.toLowerCase();
                    data = data.filter(c => 
                        (c.ticket_number && c.ticket_number.toLowerCase().includes(q)) ||
                        (c.customer_name && c.customer_name.toLowerCase().includes(q)) ||
                        (c.customer_phone && c.customer_phone.toLowerCase().includes(q))
                    );
                }

                // Frontend Status Filter
                if (filterStatus !== '') {
                    data = data.filter(c => {
                        const currentStatus = c.status || 'In Process';
                        return currentStatus.toLowerCase() === filterStatus.toLowerCase();
                    });
                }

                // Frontend Customer Type Filter (United / Outsider)
                if (filterCustomerType !== '') {
                    data = data.filter(c => {
                        const cType = c.customer_type || 'United';
                        return cType.toLowerCase() === filterCustomerType.toLowerCase();
                    });
                }

                setComplaints(data);
                if (res.data.stats) {
                    setStats(res.data.stats);
                }
            }
        } catch (err) { console.error(err); }
    }, [searchQuery, filterStatus, filterCustomerType]);

    useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

    // Live Search Bikes by Engine or Chassis
    const handleBikeQueryChange = async (val) => {
        setBikeSearchQuery(val);
        if (val.length > 1) {
            try {
                const res = await API.get(`/v1/complaints/search-bikes?query=${val}`);
                if (res.data.success) setBikeSuggestions(res.data.data);
            } catch (err) { console.error(err); }
        } else {
            setBikeSuggestions([]);
        }
    };

    // Select Bike from Suggestions Dropdown
    const handleSelectBike = (bike) => {
        setFormData({
            ...formData,
            bike_master_id: bike.bike_master_id,
            make: 'United',
            model_name: bike.model_name || '',
            color: bike.color || '',
            engine_number: bike.engine_number || '',
            chassis_number: bike.chassis_number || '',
            model_year: bike.model_year || ''
        });
        setBikeSearchQuery(bike.engine_number);
        setBikeSuggestions([]);
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await API.post('/v1/complaints', formData);
            if (res.data.success) {
                alert(`Complaint Registered! Ticket #: ${res.data.ticket_number}`);
                setView('list');
                fetchComplaints();
                setFormData({
                    customer_name: '', customer_phone: '', customer_type: 'United',
                    complaint_type: '', description: '', bike_master_id: null,
                    make: '', model_name: '', color: '', engine_number: '', chassis_number: '', model_year: ''
                });
                setBikeSearchQuery('');
            }
        } catch (err) { alert('Error registering complaint'); }
    };

    const loadDetails = async (id) => {
        try {
            const res = await API.get(`/v1/complaints/${id}`);
            if (res.data.success) {
                setSelectedComplaint(res.data.complaint);
                setFollowups(res.data.followups || []);
                setView('details');
            }
        } catch (err) { console.error(err); }
    };

    const handleAppendTimeline = async (e) => {
        e.preventDefault();
        try {
            await API.post(`/v1/complaints/${selectedComplaint.id}/followup`, newStep);
            alert('Timeline updated!');
            setNewStep({ step_title: '', comment: '', mark_satisfied: false });
            loadDetails(selectedComplaint.id);
            fetchComplaints();
        } catch (err) { alert('Failed to update timeline'); }
    };

    return (
        <div style={styles.container}>
            {/* TOP BAR / NAVIGATION */}
            <div style={styles.topNav}>
                <h2>🛠️ Complaint & Lifecycle Management</h2>
                <div>
                    {view !== 'list' && <button onClick={() => setView('list')} style={styles.btnSecondary}>&larr; Back to Dashboard</button>}
                    {view === 'list' && <button onClick={() => setView('create')} style={styles.btnPrimary}>+ Register New Complaint</button>}
                </div>
            </div>

            {/* VIEW 1: SUMMARY DASHBOARD & LIST */}
            {view === 'list' && (
                <div>
                    <div style={styles.statsRow}>
                        <div style={styles.statCard}><span>TOTAL COMPLAINTS</span><strong>{stats.total}</strong></div>
                        <div style={styles.statCard}><span>IN PROCESS</span><strong>{stats.in_process}</strong></div>
                        <div style={styles.statCard}><span>SATISFIED</span><strong>{stats.satisfied}</strong></div>
                    </div>

                    {/* FILTER ROW WITH UNITED & OUTSIDER BUTTONS */}
                    <div style={{...styles.filterRow, flexWrap: 'wrap', gap: '10px'}}>
                        <input 
                            placeholder="Search Ticket #, Name, Phone..." 
                            value={searchQuery} 
                            onChange={e => setSearchQuery(e.target.value)} 
                            style={styles.searchInput} 
                        />
                        
                        <div style={styles.tabFilters}>
                            {/* Customer Type Filters */}
                            <button onClick={() => setFilterCustomerType('')} style={filterCustomerType === '' ? styles.tabActive : styles.tab}>All Types</button>
                            <button onClick={() => setFilterCustomerType('United')} style={filterCustomerType === 'United' ? styles.tabActive : styles.tab}>United</button>
                            <button onClick={() => setFilterCustomerType('Outsider')} style={filterCustomerType === 'Outsider' ? styles.tabActive : styles.tab}>Outsider</button>
                        </div>

                        <div style={styles.tabFilters}>
                            {/* Status Filters */}
                            <button onClick={() => setFilterStatus('')} style={filterStatus === '' ? styles.tabActive : styles.tab}>All Status</button>
                            <button onClick={() => setFilterStatus('In Process')} style={filterStatus === 'In Process' ? styles.tabActive : styles.tab}>In Process</button>
                            <button onClick={() => setFilterStatus('Satisfied')} style={filterStatus === 'Satisfied' ? styles.tabActive : styles.tab}>Satisfied</button>
                        </div>
                    </div>

                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.th}>
                                <th>Ticket ID</th>
                                <th>Customer</th>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {complaints.length > 0 ? complaints.map(c => {
                                const statusVal = c.status || 'In Process';
                                const isSatisfied = statusVal.toLowerCase() === 'satisfied';
                                const categoryVal = c.category_name || 'General';

                                return (
                                    <tr key={c.id} style={styles.tr}>
                                        <td><b>{c.ticket_number}</b></td>
                                        <td>{c.customer_name} <br/><small>{c.customer_phone}</small></td>
                                        <td><span style={styles.badgeType}>{c.customer_type || 'United'}</span></td>
                                        <td>{categoryVal}</td>
                                        <td>
                                            <span style={{
                                                ...styles.statusBadge, 
                                                background: isSatisfied ? '#dcfce7' : '#fef3c7', 
                                                color: isSatisfied ? '#166534' : '#92400e'
                                            }}>
                                                {statusVal}
                                            </span>
                                        </td>
                                        <td>
                                            <button onClick={() => loadDetails(c.id)} style={styles.actionBtn}>View Full Details</button>
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan="6" style={{textAlign: 'center', padding: '20px', color: '#64748b'}}>No complaints found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* VIEW 2: REGISTER COMPLAINT FORM */}
            {view === 'create' && (
                <form onSubmit={handleCreateSubmit} style={styles.formContainer}>
                    <h3 style={{marginBottom:'20px'}}>Register Customer Complaint</h3>
                    
                    <div style={styles.grid2}>
                        <div>
                            <label style={styles.label}>Customer Name</label>
                            <input required value={formData.customer_name} onChange={e => setFormData({...formData, customer_name: e.target.value})} style={styles.input} />
                        </div>
                        <div>
                            <label style={styles.label}>Contact Number</label>
                            <input required value={formData.customer_phone} onChange={e => setFormData({...formData, customer_phone: e.target.value})} style={styles.input} />
                        </div>
                    </div>

                    <div style={styles.grid2}>
                        <div>
                            <label style={styles.label}>Customer / Status Type</label>
                            <select value={formData.customer_type} onChange={e => setFormData({...formData, customer_type: e.target.value})} style={styles.input}>
                                <option value="United">United</option>
                                <option value="Outsider">Outsider</option>
                            </select>
                        </div>
                        
                        <div>
                            <label style={styles.label}>Complaint Category <span style={{color:'red'}}>*Select from Dropdown</span></label>
                            <select required value={formData.complaint_type} onChange={e => setFormData({...formData, complaint_type: e.target.value})} style={styles.input}>
                                <option value="">-- Choose Category --</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.category_name} ({cat.department})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={{position: 'relative', marginBottom: '15px'}}>
                        <label style={styles.label}>Search Bike (Type Engine # or Chassis #) <span style={{color:'red'}}>*Select from Dropdown</span></label>
                        <input 
                            value={bikeSearchQuery} 
                            onChange={e => handleBikeQueryChange(e.target.value)} 
                            style={styles.input} 
                            placeholder="Type engine or chassis number to search..." 
                        />
                        
                        {bikeSuggestions.length > 0 && (
                            <div style={styles.dropdownList}>
                                {bikeSuggestions.map(b => (
                                    <div key={b.bike_master_id} onClick={() => handleSelectBike(b)} style={styles.dropdownItem}>
                                        <b>{b.model_name}</b> ({b.model_year}) | Engine: {b.engine_number} | Chassis: {b.chassis_number}
                                    </div>
                                ))}
                            </div>
                        )}

                        <table style={styles.tableBox}>
                            <thead>
                                <tr>
                                    <th style={styles.thTable}>Make</th>
                                    <th style={styles.thTable}>Model Name</th>
                                    <th style={styles.thTable}>Year</th>
                                    <th style={styles.thTable}>Color</th>
                                    <th style={styles.thTable}>Engine #</th>
                                    <th style={styles.thTable}>Chassis #</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={styles.tdTable}><input value={formData.make} readOnly style={styles.tableInput} /></td>
                                    <td style={styles.tdTable}><input value={formData.model_name} readOnly style={styles.tableInput} /></td>
                                    <td style={styles.tdTable}><input value={formData.model_year} readOnly style={styles.tableInput} /></td>
                                    <td style={styles.tdTable}><input value={formData.color} readOnly style={styles.tableInput} /></td>
                                    <td style={styles.tdTable}><input value={formData.engine_number} readOnly style={styles.tableInput} /></td>
                                    <td style={styles.tdTable}><input value={formData.chassis_number} readOnly style={styles.tableInput} /></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div>
                        <label style={styles.label}>Complaint Details / Description</label>
                        <textarea rows="4" required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} style={styles.input}></textarea>
                    </div>

                    <button type="submit" style={styles.submitBtn}>Register Complaint Button</button>
                </form>
            )}

            {/* VIEW 3: DETAILS & LIFECYCLE TIMELINE */}
            {view === 'details' && selectedComplaint && (() => {
                const detailsStatus = selectedComplaint.status || 'In Process';
                const isSatisfiedDetails = detailsStatus.toLowerCase() === 'satisfied';
                const detailsCategory = selectedComplaint.category_name || 'General';

                return (
                    <div style={styles.detailsLayout}>
                        {/* Left Box: Info */}
                        <div style={styles.cardBox}>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                <h3 style={{margin:0}}>{selectedComplaint.ticket_number}</h3>
                                <span style={{
                                    background: isSatisfiedDetails ? '#dcfce7' : '#fef3c7', 
                                    color: isSatisfiedDetails ? '#166534' : '#92400e',
                                    padding:'4px 10px', borderRadius:'6px', fontWeight:'bold', fontSize:'12px'
                                }}>
                                    {detailsStatus}
                                </span>
                            </div>
                            <hr style={{margin:'15px 0', borderColor:'#e2e8f0'}}/>
                            <p><b>Customer Name:</b> {selectedComplaint.customer_name}</p>
                            <p><b>Customer Type:</b> {selectedComplaint.customer_type || 'United'}</p>
                            <p><b>Phone / Contact:</b> {selectedComplaint.customer_phone}</p>
                            <p><b>Category:</b> {detailsCategory}</p>
                            <p><b>Bike Model:</b> {selectedComplaint.model_name || 'N/A'}</p>
                            <p><b>Engine #:</b> {selectedComplaint.engine_number || 'N/A'}</p>
                            <p><b>Chassis #:</b> {selectedComplaint.chassis_number || 'N/A'}</p>
                            <p><b>Description:</b> {selectedComplaint.description}</p>
                        </div>

                        {/* Right Box: Lifecycle Step-by-Step History */}
                        <div style={styles.cardBox}>
                            <h4>Lifecycle Step-by-Step History</h4>
                            <div style={styles.timelineList}>
                                {followups.map((f, i) => (
                                    <div key={i} style={styles.timelineItem}>
                                        <div style={styles.timelineDot}></div>
                                        <div>
                                            <small style={{color:'#64748b'}}>{new Date(f.created_at).toLocaleDateString()} - <b>{f.step_title}</b></small>
                                            <p style={{margin:'2px 0 0 0', fontSize:'13px'}}>{f.comment}</p>
                                            <small style={{color:'#2563eb'}}>Status: {f.status_change || 'In Process'}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Strict check: If status is Satisfied, completely hide the form and show notice */}
                            {isSatisfiedDetails ? (
                                <div style={{marginTop:'20px', padding:'15px', background:'#dcfce7', color:'#166534', borderRadius:'8px', textAlign:'center', fontWeight:'bold', border:'1px solid #bbf7d0'}}>
                                    ✔️ Ticket Closed (Satisfied). No further modifications allowed.
                                </div>
                            ) : (
                                <form onSubmit={handleAppendTimeline} style={{marginTop:'20px', borderTop:'1px solid #e2e8f0', paddingTop:'15px'}}>
                                    <h5>+ Add Step / Activity Log</h5>
                                    <input placeholder="Step Title (e.g. Courier Dispatched)" value={newStep.step_title} onChange={e => setNewStep({...newStep, step_title: e.target.value})} style={styles.input} required />
                                    <input placeholder="Description / Reference #" value={newStep.comment} onChange={e => setNewStep({...newStep, comment: e.target.value})} style={styles.input} required />
                                    
                                    <label style={{display:'flex', alignItems:'center', gap:'8px', margin:'10px 0', fontSize:'14px', cursor:'pointer'}}>
                                        <input type="checkbox" checked={newStep.mark_satisfied} onChange={e => setNewStep({...newStep, mark_satisfied: e.target.checked})} />
                                        Mark Customer Satisfied & Close Ticket
                                    </label>

                                    <button type="submit" style={styles.btnPrimary}>Append Timeline Event</button>
                                </form>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

const styles = {
    container: { padding: '20px', background: '#f8fafc', minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif' },
    topNav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '20px' },
    statCard: { background: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', textAlign: 'center' },
    filterRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' },
    searchInput: { padding: '10px', width: '250px', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' },
    tabFilters: { display: 'flex', gap: '5px' },
    tab: { padding: '8px 15px', background: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
    tabActive: { padding: '8px 15px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
    table: { width: '100%', background: '#fff', borderCollapse: 'collapse', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
    th: { background: '#f1f5f9', textAlign: 'left', padding: '12px', fontSize: '13px', color: '#475569' },
    tr: { borderBottom: '1px solid #f1f5f9', padding: '12px' },
    badgeType: { background: '#eff6ff', color: '#2563eb', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' },
    statusBadge: { padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' },
    actionBtn: { background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' },
    formContainer: { background: '#fff', padding: '30px', borderRadius: '12px', maxWidth: '800px', margin: '0 auto', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
    input: { width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' },
    label: { fontSize: '12px', fontWeight: 'bold', color: '#475569', display: 'block', marginBottom: '5px' },
    submitBtn: { background: '#64748b', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', width: '100%' },
    btnPrimary: { background: '#2563eb', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    btnSecondary: { background: '#e2e8f0', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
    detailsLayout: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
    cardBox: { background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
    timelineList: { maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', paddingLeft: '10px', borderLeft: '2px solid #2563eb' },
    timelineItem: { position: 'relative', paddingLeft: '10px' },
    timelineDot: { position: 'absolute', left: '-16px', top: '2px', width: '10px', height: '10px', background: '#2563eb', borderRadius: '50%' },
    dropdownList: { position: 'absolute', top: '70px', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', zIndex: 10, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
    dropdownItem: { padding: '10px 15px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '13px' },
    tableBox: { width: '100%', borderCollapse: 'collapse', marginTop: '5px', marginBottom: '15px', background: '#f8fafc' },
    thTable: { border: '1px solid #cbd5e1', padding: '6px', fontSize: '11px', background: '#e2e8f0', textAlign: 'left' },
    tdTable: { border: '1px solid #cbd5e1', padding: '2px' },
    tableInput: { width: '100%', border: 'none', background: 'transparent', padding: '6px', fontSize: '12px', outline: 'none' }
};

export default ComplaintsModule;